import { createSelector } from '@reduxjs/toolkit';
import type { BalancesState, JsonBalance, JsonBalanceRaw } from 'src/store/shared/balances/types';
import type { AccountView } from '@skynet/data-shared-accounts';
import { selectAccounts } from '@skynet/data-shared-accounts';
import { selectExchanges } from 'src/store/shared/exchange-list/selectors';

export interface Store {
  balances: BalancesState;
}

export const selectError = (store: Store) => store.balances.error;

/**
 * Selects raw backend balances (JsonBalanceRaw[])
 * These need to be enriched with account data before use
 */
export const selectRawBalances = (store: Store) => store.balances.list;

/**
 * Selects enriched balances with account data populated from Accounts state
 * Filters out balances where the account is missing from Accounts state
 */
export const selectBalanceHash = createSelector(
  selectRawBalances,
  selectAccounts,
  selectExchanges,
  (
    backendBalances: Record<number, JsonBalanceRaw[]>,
    accounts: AccountView[],
    exchanges,
  ): Record<number, JsonBalance[]> => {
    const result: Record<number, JsonBalance[]> = {};

    Object.keys(backendBalances).forEach((projectIdStr) => {
      const projectId = Number(projectIdStr);
      const projectBalances = backendBalances[projectId] || [];

      // Enrich each balance with account data, filter out missing accounts
      const enrichedBalances: JsonBalance[] = projectBalances
        .map((balance) => {
          const account = accounts.find((acc) => acc.name === balance.accountID);

          // Skip if account not found
          if (!account) {
            return null;
          }

          const exchange = exchanges.find((ex) => ex.id === account.exchange);

          const enriched: JsonBalance = {
            ...balance,
            account,
            accountName: account.label,
            exchangeID: account.exchange ?? 0,
            exchangeName: exchange?.name,
            isWallet: false,
          };

          return enriched;
        })
        .filter((b): b is JsonBalance => b !== null);

      result[projectId] = enrichedBalances;
    });

    return result;
  },
);
export const selectBalanceSupportedTokenObj = (store: Store) => store.balances.supportedTokens;
export const selectIsBalancesLoading = (store: Store) => store.balances.requestStatus === 'pending';

export const selectBalancesSelection = (store: Store) => store.balances.selection;
export const selectCreateSnapshotStatus = (store: Store) =>
  store.balances.createBalanceSnapshotStatus;
export const selectCreateWalletSnapshotStatus = (store: Store) =>
  store.balances.createWalletSnapshotStatus;

export const selectAddBalanceStates = (store: Store) => store.balances.addBalanceStatus;

export const selectIsBalancesCorrectionOpen = (store: Store) =>
  store.balances.balancesCorrection.open;
export const selectBalancesCorrectionData = (store: Store) =>
  store.balances.balancesCorrection.data;
